import type { LucideIcon } from "lucide-react";
import { Eye, Scissors, ShieldCheck, Truck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal } from "./Reveal";
import { Section, SectionHeading, SectionLead } from "./Section";

// preview / made / shipping read as a quiet list; the guarantee is pulled out as
// the one prominent panel. That asymmetry is deliberate: it's the hesitant-buyer
// reassurance, and it keeps this off the banned identical icon-card grid.
const PRACTICAL: { id: string; icon: LucideIcon }[] = [
  { id: "preview", icon: Eye },
  { id: "made", icon: Scissors },
  { id: "shipping", icon: Truck },
];

export function NoSurprises() {
  const { t } = useTranslation("landing");
  return (
    <Section id="trust" labelledBy="trust-heading" className="bg-card">
      <Reveal>
        <div className="max-w-2xl">
          <SectionHeading id="trust-heading">{t("trust.heading")}</SectionHeading>
          <SectionLead className="mt-4">{t("trust.lead")}</SectionLead>
        </div>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <ul className="space-y-8">
            {PRACTICAL.map(({ id, icon: Icon }) => (
              <li key={id} className="flex gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/12 text-primary">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <h3 className="font-display text-base font-semibold tracking-tight text-foreground">
                    {t(`trust.assurances.${id}.title`)}
                  </h3>
                  <p className="mt-1 text-pretty text-sm leading-relaxed text-foreground/75">
                    {t(`trust.assurances.${id}.body`)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delayMs={100}>
          <div className="flex h-full flex-col justify-center rounded-2xl border border-border bg-paper p-8 shadow-sm">
            <ShieldCheck className="h-8 w-8 text-primary" aria-hidden="true" />
            <h3 className="mt-4 font-display text-xl font-semibold tracking-tight text-foreground">
              {t("trust.assurances.guarantee.title")}
            </h3>
            <p className="mt-2 text-pretty leading-relaxed text-foreground/80">
              {t("trust.assurances.guarantee.body")}
            </p>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}
